// The artist editor's classification form (#443): what it shows, and what a save sends back.
import { arraysEqual, classificationList, parseCommaSeparated } from "../../utils/helpers";

export const CLASSIFICATION_OPTIONS = {
  experience_modalities: [
    "live_music",
    "classical_music",
    "dj_set",
    "film_screening",
    "theater_performance",
    "dance_performance",
    "comedy_performance",
    "circus_performance",
    "magic_performance",
    "exhibition_viewing",
    "talk_panel",
    "workshop_hands_on",
    "networking",
    "sports_competition",
    "market_fair",
    "food_drink",
    "community_meetup",
    "guided_tour",
    "ceremony",
    "digital_interactive",
  ],
  cultural_identity: [
    "queer",
    "feminist",
    "activist",
    "spiritual",
    "alternative",
    "avant_garde",
    "academic",
    "traditional_values",
  ],
  target_audience: [
    "children",
    "teens",
    "young_adults",
    "adults",
    "families",
    "general_public",
    "professionals",
    "students",
    "tourists",
    "locals",
  ],
  kids_policy: ["children_primary", "kids_welcome", "adults_only", "unknown"],
  primary_event_type: [
    "concert",
    "classical_concert",
    "opera",
    "club_night",
    "film_screening",
    "theater_play",
    "dance_show",
    "comedy_show",
    "circus_show",
    "magic_show",
    "exhibition",
    "talk_panel",
    "workshop",
    "conference",
    "trade_fair",
    "sports_event",
    "festival",
    "community_event",
    "tour",
    "escape_game",
    "game_event",
    "immersive_experience",
    "dining",
    "other",
  ],
};

const joinClassificationList = (value) => classificationList(value).join(", ");
const normalizeClassificationOption = (value, options) => {
  if (!value) return "";
  const normalized = String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");
  return options.find((option) => option === normalized) || "";
};
const rawClassificationValue = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item;
  return item.id || item.name || "";
};
const classificationEditList = (value, options = null) => {
  if (!Array.isArray(value)) return "";
  return value
    .map((item) => {
      const rawValue = rawClassificationValue(item);
      return options ? normalizeClassificationOption(rawValue, options) : rawValue;
    })
    .filter(Boolean)
    .join(", ");
};

export const classificationProfileToEdit = (profile = {}) => ({
  classification_source: profile.classification_source || "artist",
  // Editable only once the classifier has written them (#443): null on a score-prompt profile.
  judgments: profile.judgments ? { ...profile.judgments } : null,
  themes_subjects: joinClassificationList(profile.themes_subjects),
  experience_modalities: classificationEditList(
    profile.experience_modalities,
    CLASSIFICATION_OPTIONS.experience_modalities
  ),
  cultural_identity: classificationEditList(profile.cultural_identity, CLASSIFICATION_OPTIONS.cultural_identity),
  target_audience: classificationEditList(profile.target_audience, CLASSIFICATION_OPTIONS.target_audience),
  kids_policy: normalizeClassificationOption(profile.kids_policy, CLASSIFICATION_OPTIONS.kids_policy) || "unknown",
  spoken_language: classificationEditList(profile.spoken_language),
  primary_event_type:
    normalizeClassificationOption(profile.primary_event_type, CLASSIFICATION_OPTIONS.primary_event_type) || "other",
  inferred_context: profile.inferred_context || "",
});

export const buildClassificationProfile = (editProfile = {}, originalProfile = {}) => {
  const originalThemeNames = joinClassificationList(originalProfile.themes_subjects);
  const editedThemeNames = editProfile.themes_subjects || "";
  return {
    ...(editProfile.judgments ? { judgments: editProfile.judgments } : {}),
    themes_subjects: editedThemeNames === originalThemeNames ? originalProfile.themes_subjects || [] : parseCommaSeparated(editedThemeNames),
    experience_modalities: parseCommaSeparated(editProfile.experience_modalities),
    cultural_identity: parseCommaSeparated(editProfile.cultural_identity),
    target_audience: parseCommaSeparated(editProfile.target_audience),
    kids_policy: editProfile.kids_policy || "unknown",
    spoken_language: parseCommaSeparated(editProfile.spoken_language),
    primary_event_type: editProfile.primary_event_type || "other",
    inferred_context: editProfile.inferred_context || "",
    classification_source: editProfile.classification_source || "artist",
  };
};

// The profile keys an edit actually changed, against the stored profile put through the same edit
// round trip. The editor is lossy — it fills defaults and drops values outside its options — so
// comparing against the stored profile itself never came out equal, and every artist save resent
// the whole profile and re-propagated the artist. The API merges a partial profile.
export const changedClassificationFields = (editProfile = {}, originalProfile = {}) => {
  const next = buildClassificationProfile(editProfile, originalProfile);
  const baseline = buildClassificationProfile(classificationProfileToEdit(originalProfile), originalProfile);
  return Object.fromEntries(Object.entries(next).filter(([key, value]) => !arraysEqual(value, baseline[key])));
};
